import {Timetable} from '@app/ui-components/models/timetable';
import {environment} from 'src/environments/environment';
import {BonusType, Kind} from './kind';

export abstract class App {
  public entry = '';
  public details = '';
  /**
   * Install date as string (YYYY-MM-DD)
   */
  public installDate = '';
  public kind: Kind = Kind.AllowedBasic;
  public bonusType: BonusType = BonusType.NoBonus;

  /**
   * Container for additional data which is needed for sending the app to the API
   * but not needed in the app
   */
  public additionalData: {[key: string]: any} = {};

  protected static fromApiApp(apiApp: ApiApp, app: App): App {
    app.entry = apiApp.entry              ?? app.entry;
    app.details = apiApp.details          ?? app.details;
    app.installDate = apiApp.installDate  ?? app.installDate;
    app.kind = apiApp.kind                ?? app.kind;

    return app;
  }

  /**
   * Converts the app to an object that can be sent to the API
   * @param appType Only required for AppWeb
   */
  public toApiApp(appType?: 'cb'|'cc'): Partial<ApiApp> {
    return {
      entry: this.entry,
      details: this.details,
      installDate: this.installDate,
      kind: this.kind
    };
  }

  public abstract getIconUrl(): string;
}

export class AppCb extends App {

  public static fromApiAppCb(apiApp: ApiAppCb): AppCb {
    const app = new AppCb();
    AppCb.fromApiApp(apiApp, app);
    app.bonusType = optTypeToBonusType(apiApp.optType) ?? app.bonusType;
    return app;
  }

  public toApiApp(): Partial<ApiAppCb> {
    return {
      ...super.toApiApp(),
      optType: bonusTypeToOptType(this.bonusType)
    };
  }

  public getIconUrl(): string {
    return getAppCbIconUrl(this.details);
  }
}

export class AppCc extends App {

  public static fromApiAppCc(apiApp: ApiAppCc): AppCc {
    const app = new AppCc();
    AppCc.fromApiApp(apiApp, app);
    app.bonusType = tagIdToBonusType(apiApp.tagID) ?? app.bonusType;
    return app;
  }

  public toApiApp(): Partial<ApiAppCc> {
    return {
      ...super.toApiApp(),
      tagID: bonusTypeToTagId(this.bonusType)
    };
  }

  public getIconUrl(): string {
    return getAppCcIconUrl(this.entry);
  }
}

export class AppWeb extends App {
  // Override default value as it does not make sense for web
  public kind = Kind.AllowedDomainBasic;

  public static fromApiAppWeb(apiApp: ApiAppWebCb|ApiAppWebCc): AppWeb {
    const app = new AppWeb();
    AppWeb.fromApiApp(apiApp, app);
    if ((apiApp as ApiAppWebCb).optType) {
      app.bonusType = optTypeToBonusType((apiApp as ApiAppWebCb).optType) ?? app.bonusType;
    } else {
      app.bonusType = tagIdToBonusType((apiApp as ApiAppWebCc).tagID) ?? app.bonusType;
    }

    return app;
  }

  /**
   * Converts the app to an object that can be sent to the API
   * @param appType Returns an object for the CB or CC endpoint
   */
  public toApiApp(appType: 'cb'|'cc'): Partial<ApiAppWebCb|ApiAppWebCc> {
    const baseApp = super.toApiApp();

    if (appType === 'cb') {
      return {
        ...baseApp,
        optType: bonusTypeToOptType(this.bonusType)
      };
    } else {
      // CC
      return {
        ...baseApp,
        tagID: bonusTypeToTagId(this.bonusType)
      };
    }
  }

  public getIconUrl(): string {
    return getWebIconUrl(this.entry);
  }
}

/**
 * Limits for one app of any type (CC, CB, Web)
 */
export class AppLimits {
  /** True if time limit for day/week/month is active */
  public isTimeLimitActive = false;
  public timeLimitDay = '02:30';
  public timeLimitWeek = '20:00';
  public timeLimitMonth = '80:00';

  /** True if time limit for weekdays is active */
  public isWeekdayLimitActive = false;
  public weekdayLimits = {
    monday: '01:30',
    tuesday: '01:30',
    wednesday: '01:30',
    thursday: '01:30',
    friday: '02:00',
    saturday: '02:00',
    sunday: '01:00'
  };

  /**
   * String that determines exact times in a week in which the app is blocked.
   * Processed by Timetable component.
   */
  public blocktimes = Timetable.defaultLimitString;

  /**
   * Boolean used for optional blocked times in Allowed Plus case
   */
  public optBlocktimes = false;

}

/**
 * Bonus options which are only used for `BonusType.AlwaysAllowedWithBonus`
 */
export class AppBonus {
  /** Interval after which a bonus is granted */
  bonusInterval = '00:05';

  /** Amount of time that is granted per interval */
  bonusPerInterval = '00:10';

  /** Maximum amount of bonus time that is granted */
  maxDailyBonus = '00:30';
}

export interface AppDetails {
  URL: string;
  title?: string;
  /** -1 if no cat */
  catID: number;
  catName?: string;
  /** -1 if no age */
  age: number;
  description: string;
  source?: any; // I don't know the type
  lang?: any; // I don't know the type
  noSuggest: boolean;
  rank: number;
  responseCode: number;
}

/** Returns AppDetails with default values (for non-optional properties) */
export function appDetailsDefaults(): AppDetails {
  return {
    URL: '',
    catID: -1,
    age: -1,
    description: '',
    noSuggest: false,
    rank: 0,
    responseCode: 0
  };
}

export interface ApiApp {
  entry: string;
  details: string;
  installDate: string;
  kind: number;
}

export interface ApiAppCb extends ApiApp {
  optType: number;
}

export interface ApiAppCc extends ApiApp {
  tagID: number;
}

export interface ApiAppWebCb extends ApiApp {
  optType: number;
}

export interface ApiAppWebCc extends ApiApp {
  tagID: number;
}

/**
 * Converts optType from API to local BonusType
 */
export function optTypeToBonusType(optType: number): BonusType {
  switch (optType) {
    case 0:
      return BonusType.NoBonus;
    case 3:
      return BonusType.AlwaysAllowed;
    case 2:
      return BonusType.AlwaysAllowedNoTimer;
    case 4:
      return BonusType.AlwaysAllowedWithBonus;
    case -5:
      return BonusType.BlockExtension;
    default:
      return BonusType.NoBonus;
  }
}

/**
 * Converts BonusType to optType for API
 */
export function bonusTypeToOptType(bonusType: BonusType): number {
  switch (bonusType) {
    case BonusType.NoBonus:
      return 0;
    case BonusType.AlwaysAllowed:
      return 3;
    case BonusType.AlwaysAllowedNoTimer:
      return 2;
    case BonusType.AlwaysAllowedWithBonus:
      return 4;
    case BonusType.BlockExtension:
      return -5;
    default:
      return 0;
  }
}

/**
 * Converts tagId from API to local BonusType
 */
export function tagIdToBonusType(tagId: number): BonusType {
  switch (tagId) {
    case 0:
      return BonusType.NoBonus;
    case 7:
      return BonusType.AlwaysCountExe;
    case 3:
      return BonusType.AlwaysAllowed;
    case 1:
      return BonusType.AlwaysAllowedNoTimer;
    case 2:
      return BonusType.AlwaysAllowedWithBonus;
    default:
      return BonusType.NoBonus;
  }
}

export function bonusTypeToTagId(bonusType: BonusType): number {
  switch (bonusType) {
    case BonusType.NoBonus:
      return 0;
    case BonusType.AlwaysCountExe:
      return 7;
    case BonusType.AlwaysAllowed:
      return 3;
    case BonusType.AlwaysAllowedNoTimer:
      return 1;
    case BonusType.AlwaysAllowedWithBonus:
      return 2;
    default:
      return 0;
  }
}

/**
 * Validate whether the given string is a valid exe filename (e.g. "chrome.exe" is valid while "chrome" is invalid)
 */
export function validateExeName(text: string): boolean {
  text = text.toLowerCase();

  if (!/^([^\ !,/\\?#'"$*:]+)$/.test(text)) {
    return false;
  }

  if (!/^(.+(\.exe|\.bin))$/.test(text)) {
    return false;
  }

  return true;
}

/**
 * Validate whether the given string is a valid domain (e.g. "salfeld.de" is valid while "salfeld" is invalid)
 */
export function validateDomainName(text: string): boolean {
  text = text.toLowerCase();

  if (!/^([^\ !,/\\?#'"$*:]+)$/.test(text)) {
    return false;
  }

  // ".exe" not allowed
  if (/^(.+\.exe)$/.test(text)) {
    return false;
  }

  // "." required
  if (!/^(.+\..+)$/.test(text)) {
    return false;
  }

  return true;
}


/* **** App icon stuff (prefer the AppIconComponent instead of using these functions) **** */

const dummySession = 'sess887783995a9d401681c6567398227aac';

/**
 * Returns icon for the given app or a default app image if no app name is given.
 *
 * Note: Prefer the AppIconComponent instead of using this function
 */
export function getAppCbIconUrl(pkgname?: string): string {
  if (pkgname != null) {
    return `${environment.apiFiles}icon/apps/${dummySession}/${pkgname}.png`;
  }

  return '../assets/images/default-app.png';
}

/**
 * Returns icon for the given exe or a default exe image if no exe name is given.
 *
 * Note: Prefer the AppIconComponent instead of using this function
 */
export function getAppCcIconUrl(exename?: string): string {
  if (exename != null) {
    const img = exename.replace('.exe', '');
    if (img === 'explorer') return `${environment.apiFiles}icon/${dummySession}/${'desktop'}.png`;
    return `${environment.apiFiles}icon/${dummySession}/${img}.png`;
  }

  return '../assets/images/default-prg.png';
}

/**
 * Returns favicon for the given website or a default icon if no domain is given.
 *
 * Note: Prefer the AppIconComponent instead of using this function
 */
export function getWebIconUrl(domain?: string): string {
  if (domain != null) {
    return `${environment.apiFiles}icon/web/${dummySession}/${domain}.png`;
  }

  return '../assets/images/default-web.png';
}
